import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { toast } from "sonner";
import {
  Plus, Search, Shield, CheckCircle, XCircle, Lock, Trash2,
  Edit2, Key, MoreVertical, Eye, EyeOff, RefreshCw,
  ChevronLeft, ChevronRight, AlertTriangle,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { userCan, normalizeRole } from "@/lib/rbac";
import { Header } from "./Schools";
import { createPortal } from "react-dom";

// ── Constants ─────────────────────────────────────────────────────────────────
const PAGE_SIZE = 20;
const SI = "w-full px-3 py-2.5 rounded-lg border border-gray-200 outline-none focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10 text-sm bg-white";
const PW_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&^#_\-])[A-Za-z\d@$!%*?&^#_\-]{8,72}$/;

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Intl.DateTimeFormat("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(new Date(iso)); }
  catch { return iso?.slice(0, 16).replace("T", " ") || "—"; }
};

function validatePassword(pw) {
  if (!pw) return "Password is required.";
  if (!PW_REGEX.test(pw)) return "Min 8 chars: uppercase, lowercase, number, and special character (@$!%*?&^#_-).";
  return null;
}

// ── Shared UI ─────────────────────────────────────────────────────────────────
function PasswordInput({ value, onChange, placeholder = "Password", autoComplete = "new-password" }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input type={show ? "text" : "password"} value={value} onChange={onChange}
        placeholder={placeholder} autoComplete={autoComplete} className={`${SI} pr-10`} />
      <button type="button" onClick={() => setShow((v) => !v)}
        className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 grid place-items-center text-gray-400 hover:text-gray-600">
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
}

function StrengthBar({ password }) {
  if (!password) return null;
  let score = 0;
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[@$!%*?&^#_\-]/.test(password)) score++;
  const labels = ["", "Weak", "Fair", "Good", "Strong", "Very Strong"];
  const colors = ["", "bg-red-400", "bg-orange-400", "bg-yellow-400", "bg-green-400", "bg-emerald-500"];
  return (
    <div className="mt-1.5">
      <div className="flex gap-1">{[1,2,3,4,5].map((n) => (<div key={n} className={`h-1 flex-1 rounded-full ${score >= n ? colors[score] : "bg-gray-200"}`} />))}</div>
      <p className="text-xs text-gray-400 mt-0.5">{labels[score]}</p>
    </div>
  );
}

const STATUS_CFG = {
  active:    { label: "Active",    cls: "text-emerald-700 bg-emerald-50 border border-emerald-200" },
  inactive:  { label: "Inactive",  cls: "text-gray-600 bg-gray-100 border border-gray-200" },
  suspended: { label: "Suspended", cls: "text-red-700 bg-red-50 border border-red-200" },
};
const StatusBadge = ({ status = "active" }) => {
  const c = STATUS_CFG[status] || STATUS_CFG.active;
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${c.cls}`}>{c.label}</span>;
};

const RoleBadge = ({ role, label }) => {
  const norm = normalizeRole(role);
  const isSA = norm === "super_admin";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${isSA ? "bg-[#EEF2FF] text-[#4F46E5] border border-[#C7D2FE]" : "bg-gray-100 text-gray-700 border border-gray-200"}`}>
      {isSA && <Shield className="w-3 h-3" />}
      {label || role}
    </span>
  );
};

function Modal({ title, onClose, children, width = "max-w-lg" }) {
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onClose]);
  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div className={`bg-white rounded-2xl shadow-2xl w-full ${width} max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="font-display text-lg font-semibold text-[#0F172A]">{title}</h2>
          <button onClick={onClose} className="w-8 h-8 grid place-items-center rounded-lg text-gray-400 hover:bg-gray-100 text-xl leading-none">&times;</button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>,
    document.body
  );
}

const Field = ({ label, error, required, children }) => (
  <div>
    <label className="text-sm font-medium text-gray-700 block mb-1.5">{label}{required && <span className="text-red-500 ml-0.5">*</span>}</label>
    {children}
    {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
  </div>
);

const BtnRow = ({ onCancel, submitLabel, loading, danger }) => (
  <div className="flex gap-3 pt-2">
    <button type="button" onClick={onCancel} className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-medium hover:bg-gray-50">Cancel</button>
    <button type="submit" disabled={loading}
      className={`flex-1 px-4 py-2.5 rounded-xl text-white text-sm font-medium disabled:opacity-50 ${danger ? "bg-red-600 hover:bg-red-700" : "bg-[#0F172A] hover:bg-[#1e293b]"}`}>
      {loading ? "Working…" : submitLabel}
    </button>
  </div>
);

// ── Create User Modal ─────────────────────────────────────────────────────────
function CreateUserModal({ onClose, onCreated, roles }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "", role: roles[0]?.value || "", mobile: "", status: "active" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.name.trim())  e.name = "Full name is required.";
    if (!form.email.trim()) e.email = "Email is required.";
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = "Invalid email format.";
    const pwErr = validatePassword(form.password);
    if (pwErr) e.password = pwErr;
    if (form.password !== form.confirm) e.confirm = "Passwords do not match.";
    if (!form.role) e.role = "Role is required.";
    return e;
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setLoading(true);
    try {
      await api.post("/admin/users", { name: form.name.trim(), email: form.email.trim(), password: form.password, role: form.role, mobile: form.mobile.trim(), status: form.status });
      toast.success("User created.");
      onCreated();
      onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not create user."); }
    setLoading(false);
  };

  return (
    <Modal title="Create Admin User" onClose={onClose}>
      <form onSubmit={submit} className="grid gap-4">
        <Field label="Full name" required error={errors.name}><input value={form.name} onChange={set("name")} placeholder="Jane Smith" className={SI} /></Field>
        <Field label="Email" required error={errors.email}><input type="email" value={form.email} onChange={set("email")} placeholder="jane@example.com" className={SI} /></Field>
        <Field label="Mobile"><input type="tel" value={form.mobile} onChange={set("mobile")} placeholder="+91 99999 00000" className={SI} /></Field>
        <Field label="Role" required error={errors.role}>
          <select value={form.role} onChange={set("role")} className={SI}>
            {roles.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </Field>
        <Field label="Initial status">
          <select value={form.status} onChange={set("status")} className={SI}>
            <option value="active">Active</option>
            <option value="inactive">Inactive (cannot log in)</option>
          </select>
        </Field>
        <Field label="Password" required error={errors.password}>
          <PasswordInput value={form.password} onChange={set("password")} />
          <StrengthBar password={form.password} />
        </Field>
        <Field label="Confirm password" required error={errors.confirm}>
          <PasswordInput value={form.confirm} onChange={set("confirm")} placeholder="Repeat password" />
        </Field>
        <BtnRow onCancel={onClose} submitLabel="Create user" loading={loading} />
      </form>
    </Modal>
  );
}

// ── Edit User Modal ───────────────────────────────────────────────────────────
function EditUserModal({ user, onClose, onUpdated, currentUser, roles, canAssignRoles }) {
  const isSelf = user.id === currentUser?.id;
  const [form, setForm] = useState({ name: user.name || "", email: user.email || "", mobile: user.mobile || "", role: user.role || "", status: user.status || "active" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Full name is required."); return; }
    setLoading(true);
    try {
      await api.patch(`/admin/users/${user.id}`, { name: form.name.trim(), email: form.email.trim(), mobile: form.mobile.trim() });
      if (!isSelf && canAssignRoles && form.role !== user.role) {
        await api.patch(`/admin/users/${user.id}/role`, { role: form.role });
      }
      if (!isSelf && form.status !== (user.status || "active")) {
        await api.patch(`/admin/users/${user.id}/status`, { status: form.status });
      }
      toast.success("User updated.");
      onUpdated();
      onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Update failed."); }
    setLoading(false);
  };

  return (
    <Modal title={`Edit — ${user.name}`} onClose={onClose}>
      <form onSubmit={submit} className="grid gap-4">
        <Field label="Full name" required><input value={form.name} onChange={set("name")} className={SI} /></Field>
        <Field label="Email"><input type="email" value={form.email} onChange={set("email")} className={SI} /></Field>
        <Field label="Mobile"><input type="tel" value={form.mobile} onChange={set("mobile")} className={SI} /></Field>
        {canAssignRoles && !isSelf && (
          <Field label="Role">
            <select value={form.role} onChange={set("role")} className={SI}>
              {roles.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </Field>
        )}
        {!isSelf && (
          <Field label="Account status">
            <select value={form.status} onChange={set("status")} className={SI}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
            </select>
          </Field>
        )}
        {isSelf && (
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            You cannot change your own role or status.
          </div>
        )}
        <BtnRow onCancel={onClose} submitLabel="Save changes" loading={loading} />
      </form>
    </Modal>
  );
}

// ── Reset Password Modal ──────────────────────────────────────────────────────
function ResetPasswordModal({ user, onClose }) {
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    const pwErr = validatePassword(form.password);
    if (pwErr) errs.password = pwErr;
    if (form.password !== form.confirm) errs.confirm = "Passwords do not match.";
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setLoading(true);
    try {
      await api.patch(`/admin/users/${user.id}/password`, { password: form.password });
      toast.success(`Password reset for ${user.name}.`);
      onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Reset failed."); }
    setLoading(false);
  };

  return (
    <Modal title={`Reset password — ${user.name}`} onClose={onClose}>
      <p className="text-sm text-gray-500 mb-4">The user will need to use this new password on their next login.</p>
      <form onSubmit={submit} className="grid gap-4">
        <Field label="New password" required error={errors.password}>
          <PasswordInput value={form.password} onChange={set("password")} />
          <StrengthBar password={form.password} />
        </Field>
        <Field label="Confirm password" required error={errors.confirm}>
          <PasswordInput value={form.confirm} onChange={set("confirm")} placeholder="Repeat password" />
        </Field>
        <BtnRow onCancel={onClose} submitLabel="Reset password" loading={loading} danger />
      </form>
    </Modal>
  );
}

// ── Confirm Delete Modal ──────────────────────────────────────────────────────
function ConfirmDeleteModal({ user, onClose, onDeleted }) {
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setLoading(true);
    try {
      await api.delete(`/admin/users/${user.id}`);
      toast.success(`${user.name} deleted.`);
      onDeleted();
      onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed."); }
    setLoading(false);
  };
  return (
    <Modal title="Delete user" onClose={onClose} width="max-w-sm">
      <div className="text-center grid gap-4">
        <div className="w-12 h-12 rounded-full bg-red-50 grid place-items-center mx-auto">
          <Trash2 className="w-6 h-6 text-red-500" />
        </div>
        <div>
          <p className="font-medium text-[#0F172A]">Delete {user.name}?</p>
          <p className="text-sm text-gray-500 mt-1">This cannot be undone. The user will lose all access immediately.</p>
        </div>
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-medium hover:bg-gray-50">Cancel</button>
          <button onClick={submit} disabled={loading} className="flex-1 px-4 py-2.5 rounded-xl bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50">
            {loading ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ── Row Action Menu ───────────────────────────────────────────────────────────
function ActionMenu({ user, currentUser, canWrite, canReset, onEdit, onReset, onDelete, onStatus }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const menuRef = useRef(null);
  const isSelf = user.id === currentUser?.id;
  const status = user.status || "active";

  useEffect(() => {
    const h = (e) => { if (!ref.current?.contains(e.target) && !menuRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const MenuItem = ({ icon: Icon, label, onClick, danger = false, disabled = false }) => (
    <button disabled={disabled} onClick={() => { setOpen(false); onClick(); }}
      className={`w-full flex items-center gap-2.5 px-4 py-2 text-sm transition-colors ${disabled ? "opacity-40 cursor-not-allowed" : "hover:bg-gray-50"} ${danger ? "text-red-600" : "text-gray-700"}`}>
      <Icon className="w-3.5 h-3.5 shrink-0" /> {label}
    </button>
  );

  const rect = ref.current?.getBoundingClientRect();
  const menuStyle = rect ? { top: rect.bottom + 4, left: Math.max(4, rect.right - 184) } : {};

  const hasActions = canWrite || (canReset && !isSelf);
  if (!hasActions) return null;

  return (
    <div ref={ref} className="relative inline-block">
      <button onClick={() => setOpen((v) => !v)} data-testid={`actions-${user.id}`}
        className="w-8 h-8 grid place-items-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600">
        <MoreVertical className="w-4 h-4" />
      </button>
      {open && createPortal(
        <div ref={menuRef} className="fixed z-[200] bg-white rounded-xl border border-gray-200 shadow-xl py-1 w-46 min-w-[180px]"
          style={menuStyle}>
          {canWrite && <MenuItem icon={Edit2} label="Edit user" onClick={onEdit} />}
          {canReset && !isSelf && <MenuItem icon={Key} label="Reset password" onClick={onReset} />}
          {canWrite && !isSelf && status !== "active"   && <MenuItem icon={CheckCircle} label="Activate"   onClick={() => onStatus("active")} />}
          {canWrite && !isSelf && status === "active"   && <MenuItem icon={XCircle}     label="Deactivate" onClick={() => onStatus("inactive")} />}
          {canWrite && !isSelf && status !== "suspended"&& <MenuItem icon={Lock}        label="Suspend"    onClick={() => onStatus("suspended")} danger />}
          {canWrite && !isSelf && <div className="border-t border-gray-100 my-1" />}
          {canWrite && !isSelf && <MenuItem icon={Trash2} label="Delete user" onClick={onDelete} danger />}
        </div>,
        document.body
      )}
    </div>
  );
}

// ── Pagination ────────────────────────────────────────────────────────────────
function Pagination({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between mt-4 px-1">
      <span className="text-xs text-gray-500">Page {page} of {totalPages}</span>
      <div className="flex items-center gap-1">
        <button onClick={() => onChange(page - 1)} disabled={page === 1}
          className="w-8 h-8 grid place-items-center rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">
          <ChevronLeft className="w-4 h-4" />
        </button>
        {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
          const p = totalPages <= 7 ? i + 1 : (page <= 4 ? i + 1 : page - 3 + i);
          if (p < 1 || p > totalPages) return null;
          return (
            <button key={p} onClick={() => onChange(p)}
              className={`w-8 h-8 grid place-items-center rounded-lg text-sm ${p === page ? "bg-[#0F172A] text-white" : "border border-gray-200 hover:bg-gray-50"}`}>
              {p}
            </button>
          );
        })}
        <button onClick={() => onChange(page + 1)} disabled={page === totalPages}
          className="w-8 h-8 grid place-items-center rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function UserManagement() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);   // [{value, label, permissions}]
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState("");
  const [filterRole, setFilterRole]     = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [page, setPage] = useState(1);
  const [modal, setModal]     = useState(null);
  const [selected, setSelected] = useState(null);

  const canWrite    = userCan(currentUser, "users:write");
  const canReset    = userCan(currentUser, "users:write");
  const canAssign   = userCan(currentUser, "roles:assign");

  // ── Load users ──────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch { toast.error("Could not load users."); }
    setLoading(false);
  }, []);

  // ── Load roles from backend ─────────────────────────────────────────────────
  const loadRoles = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/roles");
      // data: { super_admin: { permissions: [...] }, ... }
      const ROLE_ORDER = ["super_admin", "org_admin", "coordinator", "trainer", "principal", "teacher", "viewer"];
      const ROLE_LABELS_MAP = {
        super_admin: "Super Admin", org_admin: "Organization Admin",
        coordinator: "Coordinator", trainer: "Trainer",
        principal: "Principal", teacher: "Teacher", viewer: "Viewer",
      };
      const sorted = Object.keys(data).sort((a, b) => {
        const ia = ROLE_ORDER.indexOf(a), ib = ROLE_ORDER.indexOf(b);
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      });
      setRoles(sorted.map((r) => ({ value: r, label: ROLE_LABELS_MAP[r] || r, permissions: data[r]?.permissions || [] })));
    } catch {
      // Fallback to static list if roles API unavailable
      setRoles([
        { value: "super_admin", label: "Super Admin" },
        { value: "org_admin",   label: "Organization Admin" },
        { value: "coordinator", label: "Coordinator" },
        { value: "trainer",     label: "Trainer" },
        { value: "principal",   label: "Principal" },
        { value: "teacher",     label: "Teacher" },
        { value: "viewer",      label: "Viewer" },
      ]);
    }
  }, []);

  useEffect(() => { load(); loadRoles(); }, [load, loadRoles]);

  // ── User name lookup map for createdBy ──────────────────────────────────────
  const userMap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u.name])), [users]);
  const roleLabel = (r) => roles.find((x) => x.value === normalizeRole(r))?.label || r;

  // ── Filtered + paginated list ───────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return users.filter((u) => {
      const matchQ = !q || u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) || u.mobile?.includes(q);
      const matchR = !filterRole   || normalizeRole(u.role) === filterRole;
      const matchS = !filterStatus || (u.status || "active") === filterStatus;
      return matchQ && matchR && matchS;
    });
  }, [users, search, filterRole, filterStatus]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageUsers  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Reset page on filter change
  useEffect(() => setPage(1), [search, filterRole, filterStatus]);

  const openModal  = (name, u = null) => { setSelected(u); setModal(name); };
  const closeModal = () => { setModal(null); setSelected(null); };

  const handleStatus = async (user, newStatus) => {
    try {
      await api.patch(`/admin/users/${user.id}/status`, { status: newStatus });
      toast.success(`User ${newStatus === "active" ? "activated" : newStatus === "inactive" ? "deactivated" : "suspended"}.`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Status change failed."); }
  };

  return (
    <div className="fade-up">
      <Header
        title="User Management"
        subtitle={`${users.length} account${users.length !== 1 ? "s" : ""}`}
        actions={
          <div className="flex flex-wrap gap-2">
            {canWrite && (
              <button onClick={() => openModal("create")} data-testid="create-user-btn"
                className="inline-flex items-center gap-1.5 bg-[#0F172A] text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-[#1e293b]">
                <Plus className="w-4 h-4" /> New user
              </button>
            )}
          </div>
        }
      />

      {/* Search & filter */}
      <div className="mt-6 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, mobile…"
            className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-gray-200 outline-none focus:border-[#4F46E5] text-sm" />
        </div>
        <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)}
          className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm bg-white">
          <option value="">All roles</option>
          {roles.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm bg-white">
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="suspended">Suspended</option>
        </select>
        <button onClick={load} title="Refresh" className="w-10 h-10 grid place-items-center rounded-lg border border-gray-200 hover:bg-gray-50">
          <RefreshCw className="w-4 h-4 text-gray-500" />
        </button>
      </div>

      {/* Table */}
      <div className="mt-4 rounded-2xl border border-gray-200 bg-white overflow-x-auto">
        {loading ? (
          <div className="p-12 text-center text-gray-400 text-sm">Loading users…</div>
        ) : pageUsers.length === 0 ? (
          <div className="p-12 text-center text-gray-400 text-sm">No users match your filters.</div>
        ) : (
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="border-b border-gray-100 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                <th className="text-left px-4 py-3">User</th>
                <th className="text-left px-4 py-3">Role</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Last login</th>
                <th className="text-left px-4 py-3">Created</th>
                <th className="text-left px-4 py-3">Created by</th>
                <th className="px-4 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {pageUsers.map((u) => (
                <tr key={u.id} className="hover:bg-gray-50/50 transition-colors" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-400 to-violet-500 grid place-items-center shrink-0 text-white text-xs font-bold">
                        {(u.name || "?")[0].toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-[#0F172A] flex items-center gap-1.5 flex-wrap">
                          {u.name}
                          {u.id === currentUser?.id && <span className="text-xs text-[#4F46E5] font-normal">(you)</span>}
                        </div>
                        <div className="text-xs text-gray-400 truncate">{u.email}</div>
                        {u.mobile && <div className="text-xs text-gray-400">{u.mobile}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3"><RoleBadge role={u.role} label={roleLabel(u.role)} /></td>
                  <td className="px-4 py-3"><StatusBadge status={u.status || "active"} /></td>
                  <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{fmt(u.last_login_at)}</td>
                  <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{fmt(u.created_at)}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{u.created_by ? (userMap[u.created_by] || u.created_by.slice(0, 8) + "…") : "System"}</td>
                  <td className="px-4 py-3">
                    <ActionMenu
                      user={u} currentUser={currentUser}
                      canWrite={canWrite} canReset={canReset}
                      onEdit={() => openModal("edit", u)}
                      onReset={() => openModal("reset", u)}
                      onDelete={() => openModal("delete", u)}
                      onStatus={(s) => handleStatus(u, s)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="flex items-center justify-between mt-2 px-1">
        <p className="text-xs text-gray-400">{filtered.length} of {users.length} users</p>
        <Pagination page={page} totalPages={totalPages} onChange={setPage} />
      </div>

      {/* Modals */}
      {modal === "create" && <CreateUserModal onClose={closeModal} onCreated={load} roles={roles} />}
      {modal === "edit"   && selected && <EditUserModal user={selected} onClose={closeModal} onUpdated={load} currentUser={currentUser} roles={roles} canAssignRoles={canAssign} />}
      {modal === "reset"  && selected && <ResetPasswordModal user={selected} onClose={closeModal} />}
      {modal === "delete" && selected && <ConfirmDeleteModal user={selected} onClose={closeModal} onDeleted={load} />}
    </div>
  );
}
